import { useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useAdminClient } from '@/hooks/useAdmin'
import { useUIStore } from '@/store/ui'
import { AdminError } from './AdminError'
import { ClientFormModal } from './ClientFormModal'
import { ClientMembers } from './ClientMembers'
import { CoverageEditor } from './CoverageEditor'
import { PipelineJobsPanel } from './PipelineJobsPanel'
import { TopicWeightsEditor } from './TopicWeightsEditor'

type Tab = 'overview' | 'coverage' | 'topics' | 'members' | 'briefs' | 'jobs'

const TABS: Tab[] = ['overview', 'coverage', 'topics', 'members', 'briefs', 'jobs']

const STATUS_CLS: Record<string, string> = {
  ready:      'status--ready',
  generating: 'status--generating',
  pending:    'status--pending',
  error:      'status--error',
}

export function AdminClientView() {
  const { clientId } = useParams<{ clientId: string }>()
  const navigate = useNavigate()
  const setViewingAs = useUIStore((s) => s.setViewingAs)
  const { data, isLoading, error } = useAdminClient(clientId)
  const [tab, setTab] = useState<Tab>('overview')
  const [editing, setEditing] = useState(false)

  if (isLoading) return <div className="admin-loading">Loading…</div>
  // A non-admin session fails at RLS rather than returning a row — show why.
  if (error) return <AdminError error={error} title="Could not load client" />
  if (!data?.client) return <div className="admin-error">Client not found.</div>

  const { client, regions, topicWeights, recentStories, balance } = data

  function enterPortal() {
    setViewingAs(client)
    navigate('/app')
  }

  return (
    <div className="admin-client-view">
      <div className="admin-breadcrumb">
        <Link to="/admin">Dashboard</Link>
        <span>/</span>
        <span>{client.display_name}</span>
      </div>

      <div className="admin-client-header">
        <div className="admin-client-header__info">
          <div className="admin-client-header__accent" style={{ background: client.accent_color }} />
          <div>
            <h1 className="admin-client-header__name">{client.display_name}</h1>
            <p className="admin-client-header__location">{client.city_state} · {client.timezone}</p>
          </div>
          <span className={`active-badge ${client.active ? 'active-badge--on' : 'active-badge--off'}`}>
            {client.active ? 'Active' : 'Inactive'}
          </span>
        </div>
        <div className="admin-client-header__actions">
          <NewsletterSwitch clientId={client.id} enabled={!!client.newsletter_enabled} planId={client.plan_id} />
          <button className="btn-secondary" onClick={() => setEditing(true)}>Edit</button>
          <button className="btn-primary btn--amber" onClick={enterPortal}>
            Enter portal →
          </button>
        </div>
      </div>

      {editing && <ClientFormModal client={client} onClose={() => setEditing(false)} />}

      <div className="admin-tabs">
        {TABS.map((t) => (
          <button
            key={t}
            className={`admin-tab ${tab === t ? 'admin-tab--active' : ''}`}
            onClick={() => setTab(t)}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <div className="admin-section">
          <div className="admin-meta-grid">
            <div className="admin-meta-item">
              <span className="admin-meta-label">Slug</span>
              <span className="admin-meta-value mono">{client.slug}</span>
            </div>
            <div className="admin-meta-item">
              <span className="admin-meta-label">State</span>
              <span className="admin-meta-value">{client.state}</span>
            </div>
            <div className="admin-meta-item">
              <span className="admin-meta-label">Timezone</span>
              <span className="admin-meta-value">{client.timezone}</span>
            </div>
            <div className="admin-meta-item">
              <span className="admin-meta-label">Accent colour</span>
              <span className="admin-meta-value">
                <span className="colour-swatch" style={{ background: client.accent_color }} />
                {client.accent_color}
              </span>
            </div>
            <div className="admin-meta-item">
              <span className="admin-meta-label">Regions</span>
              <span className="admin-meta-value">{regions.length}</span>
            </div>
            <div className="admin-meta-item">
              <span className="admin-meta-label">Topic weights</span>
              <span className="admin-meta-value">{topicWeights.length}</span>
            </div>
            <div className="admin-meta-item">
              <span className="admin-meta-label">Seeds available</span>
              <span className="admin-meta-value">
                {balance ? `${balance.total_available} / ${balance.seeds_week}` : 'no plan'}
              </span>
            </div>
            <div className="admin-meta-item">
              <span className="admin-meta-label">Stories built</span>
              <span className="admin-meta-value">{recentStories.length}</span>
            </div>
          </div>
        </div>
      )}

      {tab === 'coverage' && <CoverageEditor clientId={client.id} regions={regions} />}

      {tab === 'topics' && <TopicWeightsEditor clientId={client.id} topicWeights={topicWeights} />}

      {tab === 'members' && <ClientMembers clientId={client.id} />}

      {tab === 'briefs' && (
        <div className="admin-section">
          <h2 className="admin-section-title">Recent Stories</h2>
          {recentStories.length === 0 ? (
            <p className="admin-empty-hint">No stories built yet.</p>
          ) : (
            <table className="admin-briefs-table">
              <thead>
                <tr>
                  <th>Built</th>
                  <th>Headline</th>
                  <th>Topic</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {recentStories.map((story) => (
                  <tr key={story.id}>
                    <td className="mono">{new Date(story.created_at).toLocaleDateString()}</td>
                    <td>{story.headline}</td>
                    <td className="mono">{story.category}</td>
                    <td>
                      <span className={`status-pill ${STATUS_CLS[story.status] ?? ''}`}>
                        {story.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === 'jobs' && <PipelineJobsPanel clientId={client.id} />}
    </div>
  )
}

/** The newsletter add-on, per account. Grove and Harvest include it; on Sprout an
 *  admin switches it on until billing for add-ons exists. */
function NewsletterSwitch({ clientId, enabled, planId }: { clientId: string; enabled: boolean; planId: string | null }) {
  const qc = useQueryClient()
  const [busy, setBusy] = useState(false)
  if (planId === 'grove' || planId === 'harvest') return <span className="active-badge active-badge--on">Newsletter included</span>
  return (
    <button className="btn-secondary" disabled={busy} onClick={async () => {
      setBusy(true)
      const { error } = await supabase.from('clients').update({ newsletter_enabled: !enabled }).eq('id', clientId)
      setBusy(false)
      if (error) { window.alert(error.message); return }
      qc.invalidateQueries()
    }}>
      Newsletter: {enabled ? 'on' : 'off'}
    </button>
  )
}
